import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import { adminGetReports, adminReviewReport } from '../lib/api';

interface Reporter {
  _id: string;
  username: string;
  avatar?: string;
}

interface ReportTarget {
  _id: string;
  username?: string;
  text?: string;
  deleted?: boolean;
  shadowBanned?: boolean;
  suspendedUntil?: string;
}

interface Report {
  _id: string;
  reporter: Reporter;
  targetType: 'user' | 'post' | 'comment';
  target: ReportTarget;
  reason?: string;
  status: 'open' | 'reviewed';
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = 'all' | 'open' | 'reviewed';
type FilterTargetType = 'all' | 'user' | 'post' | 'comment';

export default function AdminReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('open');
  const [filterTargetType, setFilterTargetType] = useState<FilterTargetType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  const canUse = !!user?.isAdmin;

  useEffect(() => {
    if (canUse) {
      loadReports();
    }
  }, [canUse, filterStatus, filterTargetType]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterTargetType !== 'all') params.targetType = filterTargetType;

      const data = await adminGetReports(params);
      setReports(data.reports || []);
    } catch (err: any) {
      console.error('Reports yükleme hatası:', err);
      setError(err.message || 'Raporlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (reportId: string) => {
    try {
      setReviewingId(reportId);
      await adminReviewReport(reportId);

      // Update local state
      setReports((prev) =>
        prev.map((r) => (r._id === reportId ? { ...r, status: 'reviewed' as const } : r))
      );
      setSelectedReport(null);
      alert('Rapor başarıyla işaretlendi');
    } catch (err: any) {
      console.error('Review hatası:', err);
      alert('İşlem başarısız: ' + err.message);
    } finally {
      setReviewingId(null);
    }
  };

  const handleDismiss = async (reportId: string) => {
    if (!confirm('Bu raporu kapatmak istediğinizden emin misiniz?')) {
      return;
    }
    await handleReview(reportId);
  };

  const handleViewTarget = (report: Report) => {
    if (report.targetType === 'post' && report.target?._id) {
      navigate(`/p/${report.target._id}`);
    } else if (report.targetType === 'user' && report.target?.username) {
      navigate(`/u/${report.target.username}`);
    }
  };

  const filteredReports = reports.filter((report) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      report.reporter?.username?.toLowerCase().includes(query) ||
      report.target?.username?.toLowerCase().includes(query) ||
      report.target?.text?.toLowerCase().includes(query) ||
      report._id.toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status: string) => {
    if (status === 'open') {
      return (
        <span
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            background: '#fee2e2',
            color: '#dc2626',
          }}
        >
          🔴 Açık
        </span>
      );
    }
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 10px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          background: '#d1fae5',
          color: '#059669',
        }}
      >
        ✅ İncelendi
      </span>
    );
  };

  const getTargetTypeBadge = (type: string) => {
    const badges: Record<string, { icon: string; color: string; bg: string }> = {
      user: { icon: '👤', color: '#3b82f6', bg: '#dbeafe' },
      post: { icon: '📝', color: '#8b5cf6', bg: '#ede9fe' },
      comment: { icon: '💬', color: '#10b981', bg: '#d1fae5' },
    };
    const badge = badges[type] || { icon: '❓', color: '#6b7280', bg: '#f3f4f6' };
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 10px',
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 600,
          background: badge.bg,
          color: badge.color,
        }}
      >
        {badge.icon} {type === 'user' ? 'Kullanıcı' : type === 'post' ? 'Post' : 'Yorum'}
      </span>
    );
  };

  if (loading && reports.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          color: 'var(--text)',
        }}
      >
        <div className="spinner" style={{ marginBottom: 16 }} />
        <p style={{ color: 'var(--muted)' }}>Raporlar yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, maxWidth: 600, margin: '0 auto' }}>
        <div
          style={{
            background: 'var(--bg2)',
            border: '1px solid var(--danger)',
            borderRadius: 12,
            padding: 20,
            textAlign: 'center',
          }}
        >
          <h3 style={{ color: 'var(--danger)', marginBottom: 8 }}>⚠️ Hata</h3>
          <p style={{ color: 'var(--text)', marginBottom: 16 }}>{error}</p>
          <button onClick={loadReports} className="btn-primary">
            🔄 Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <h1
          style={{
            fontSize: 32,
            fontWeight: 'bold',
            color: 'var(--text)',
            margin: 0,
          }}
        >
          📋 Kullanıcı Raporları
        </h1>
        <button
          onClick={loadReports}
          style={{
            padding: '8px 16px',
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 8,
            color: 'var(--text)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* Filters */}
      <div
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--muted)',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Durum
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['all', 'open', 'reviewed'] as FilterStatus[]).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--muted)',
                    background: filterStatus === status ? 'var(--primary)' : 'var(--bg)',
                    color: filterStatus === status ? '#fff' : 'var(--text)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {status === 'all' ? 'Tümü' : status === 'open' ? 'Açık' : 'İncelendi'}
                </button>
              ))}
            </div>
          </div>

          {/* Target Type Filter */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Hedef Türü
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['all', 'user', 'post', 'comment'] as FilterTargetType[]).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterTargetType(type)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--muted)',
                    background: filterTargetType === type ? 'var(--primary)' : 'var(--bg)',
                    color: filterTargetType === type ? '#fff' : 'var(--text)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {type === 'all' ? 'Tümü' : type === 'user' ? 'Kullanıcı' : type === 'post' ? 'Post' : 'Yorum'}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Arama
            </label>
            <input
              type="text"
              placeholder="Kullanıcı adı, içerik..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--muted)',
                background: 'var(--bg)',
                color: 'var(--text)',
                fontSize: 14,
              }}
            />
          </div>
        </div>

        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Toplam {filteredReports.length} rapor bulundu
        </div>
      </div>

      {/* Reports List */}
      {filteredReports.length === 0 ? (
        <div
          style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 48,
            textAlign: 'center',
            color: 'var(--muted)',
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <p style={{ fontSize: 16, margin: 0 }}>Rapor bulunamadı</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredReports.map((report) => (
            <div
              key={report._id}
              style={{
                background: 'var(--bg2)',
                border: '1px solid var(--muted)',
                borderRadius: 12,
                padding: 16,
              }}
            >
              {/* Report Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: 'var(--text)',
                    }}
                  >
                    {report.reporter?.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--text)' }}>
                      <span style={{ color: 'var(--muted)' }}>Raporlayan:</span>{' '}
                      <span
                        style={{ color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}
                        onClick={() => navigate(`/u/${report.reporter.username}`)}
                      >
                        @{report.reporter.username}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {new Date(report.createdAt).toLocaleString('tr-TR')}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {getTargetTypeBadge(report.targetType)}
                  {getStatusBadge(report.status)}
                </div>
              </div>

              {/* Target Info */}
              <div
                style={{
                  background: 'var(--bg)',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
                  {report.targetType === 'user' ? 'Şikayet Edilen Kullanıcı:' : 'Şikayet Edilen İçerik:'}
                </div>
                {report.targetType === 'user' ? (
                  <div>
                    <span
                      style={{ fontSize: 14, color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => handleViewTarget(report)}
                    >
                      @{report.target?.username || 'Bilinmiyor'}
                    </span>
                    {report.target?.shadowBanned && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#f59e0b' }}>👻 Shadowban</span>
                    )}
                    {report.target?.suspendedUntil && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: '#f59e0b' }}>⏸️ Askıda</span>
                    )}
                  </div>
                ) : (
                  <div>
                    <p
                      style={{
                        fontSize: 14,
                        color: 'var(--text)',
                        margin: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {report.target?.text || '(İçerik yok)'}
                    </p>
                    {report.target?.deleted && (
                      <span style={{ fontSize: 12, color: '#ef4444', marginTop: 4, display: 'block' }}>
                        ⚠️ Bu içerik silinmiş
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Reason */}
              {report.reason && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>Sebep:</div>
                  <p style={{ fontSize: 14, color: 'var(--text)', margin: 0, fontStyle: 'italic' }}>
                    "{report.reason}"
                  </p>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setSelectedReport(report)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--muted)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  👁️ Detay
                </button>
                <button
                  onClick={() => handleViewTarget(report)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: '1px solid var(--primary)',
                    background: 'var(--primary)',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  🔗 Görüntüle
                </button>
                {report.status === 'open' && (
                  <>
                    <button
                      onClick={() => handleReview(report._id)}
                      disabled={reviewingId === report._id}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1px solid #10b981',
                        background: '#10b981',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: reviewingId === report._id ? 'not-allowed' : 'pointer',
                        opacity: reviewingId === report._id ? 0.6 : 1,
                      }}
                    >
                      {reviewingId === report._id ? '⏳' : '✅'} İncelendi
                    </button>
                    <button
                      onClick={() => handleDismiss(report._id)}
                      disabled={reviewingId === report._id}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: '1px solid #6b7280',
                        background: '#6b7280',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: reviewingId === report._id ? 'not-allowed' : 'pointer',
                        opacity: reviewingId === report._id ? 0.6 : 1,
                      }}
                    >
                      ❌ Kapat
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedReport && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 999,
            }}
            onClick={() => setSelectedReport(null)}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 600,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              zIndex: 1000,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 'bold', color: 'var(--text)', margin: 0 }}>
                📋 Rapor Detayı
              </h2>
              <button
                onClick={() => setSelectedReport(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 24,
                  color: 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Reporter */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
                  Raporlayan Kullanıcı
                </div>
                <div style={{ fontSize: 14, color: 'var(--text)' }}>
                  <span
                    style={{ color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}
                    onClick={() => {
                      navigate(`/u/${selectedReport.reporter.username}`);
                      setSelectedReport(null);
                    }}
                  >
                    @{selectedReport.reporter.username}
                  </span>
                </div>
              </div>

              {/* Target Type & Status */}
              <div style={{ display: 'flex', gap: 8 }}>
                {getTargetTypeBadge(selectedReport.targetType)}
                {getStatusBadge(selectedReport.status)}
              </div>

              {/* Target */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
                  Hedef {selectedReport.targetType === 'user' ? 'Kullanıcı' : 'İçerik'}
                </div>
                <div
                  style={{
                    background: 'var(--bg)',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  {selectedReport.targetType === 'user' ? (
                    <span
                      style={{ fontSize: 14, color: 'var(--primary)', fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => {
                        handleViewTarget(selectedReport);
                        setSelectedReport(null);
                      }}
                    >
                      @{selectedReport.target?.username || 'Bilinmiyor'}
                    </span>
                  ) : (
                    <p style={{ fontSize: 14, color: 'var(--text)', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {selectedReport.target?.text || '(İçerik yok)'}
                    </p>
                  )}
                </div>
              </div>

              {/* Reason */}
              {selectedReport.reason && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>
                    Şikayet Sebebi
                  </div>
                  <p
                    style={{
                      fontSize: 14,
                      color: 'var(--text)',
                      margin: 0,
                      fontStyle: 'italic',
                      background: 'var(--bg)',
                      padding: 12,
                      borderRadius: 8,
                    }}
                  >
                    "{selectedReport.reason}"
                  </p>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', marginBottom: 6 }}>Tarih Bilgisi</div>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>
                  <div>Oluşturulma: {new Date(selectedReport.createdAt).toLocaleString('tr-TR')}</div>
                  <div>Son Güncelleme: {new Date(selectedReport.updatedAt).toLocaleString('tr-TR')}</div>
                </div>
              </div>

              {/* Actions */}
              {selectedReport.status === 'open' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => {
                      handleReview(selectedReport._id);
                      setSelectedReport(null);
                    }}
                    disabled={reviewingId === selectedReport._id}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#10b981',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: reviewingId === selectedReport._id ? 'not-allowed' : 'pointer',
                      opacity: reviewingId === selectedReport._id ? 0.6 : 1,
                    }}
                  >
                    ✅ İncelendi Olarak İşaretle
                  </button>
                  <button
                    onClick={() => {
                      handleDismiss(selectedReport._id);
                      setSelectedReport(null);
                    }}
                    disabled={reviewingId === selectedReport._id}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: 'none',
                      background: '#6b7280',
                      color: '#fff',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: reviewingId === selectedReport._id ? 'not-allowed' : 'pointer',
                      opacity: reviewingId === selectedReport._id ? 0.6 : 1,
                    }}
                  >
                    ❌ Raporu Kapat
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
